import {
  CHARTING_STATUSES,
  chartingStatusOf,
  isWithdrawnProcedureOrder,
  orderProcedureStatus,
  useOrderSet,
  type VisitDto,
} from "@repo/data-store";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@repo/ui/components/ui/alert-dialog";
import { Button } from "@repo/ui/components/ui/button";
import { Card as UICard, CardContent as UICardContent, CardHeader as UICardHeader } from "@repo/ui/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@repo/ui/components/ui/dropdown-menu";
import { Progress, ProgressLabel } from "@repo/ui/components/ui/progress";
import { cn } from "@repo/ui/lib/utils";
import { Link } from "@tanstack/react-router";
import { CpuIcon, EllipsisIcon, MapPinIcon } from "lucide-react";
import { createContext, Fragment, use, useMemo, useState } from "react";
import { toast } from "sonner";
import { protocolDefinitionRouteId } from "@/features/catalog";
import { getAppointmentActionStates, RescheduleDialog } from "@/features/scheduling";
import type { PresentedOrderSetDto } from "@/lib/order-set-presentation";
import { PractitionerDisplay } from "@/shared/common/practitioner-display";
import { useNow } from "@/shared/lib/clock-store";
import { formatDateCompact, formatDateFull, formatTime } from "@/shared/lib/date-formats";
import { visitCompletionReason } from "@/shared/lib/visit-completion";
import { AppointmentStatusBadge } from "@/shared/status/appointment-status-badge";
import {
  ACCENTS,
  TimelinePanelLayout,
  TimelineProvider,
  type TimelineSurface,
  timelineSubtext,
} from "@/shared/timeline";
import { useDataStoreVisitActions, type VisitLifecycleActions } from "../hooks/use-data-store-visit-actions";
import { projectVisitElapsed } from "../lib/visit-elapsed";
import { AppointmentTimeline } from "./appointment-timeline";
import { JoinVisitButton } from "./join-visit-button";

// ---------- Context interface ----------

export type VisitDetailMenuItem = {
  label: string;
  allowed: boolean;
  reason?: string;
  variant?: "destructive";
  onClick: () => void;
};

export interface VisitDetailContextValue {
  state: { visit: VisitDto; orderSet?: PresentedOrderSetDto };
  actions: { menuItems: VisitDetailMenuItem[] };
}

const VisitDetailContext = createContext<VisitDetailContextValue | null>(null);

function useVisitDetailContext() {
  const ctx = use(VisitDetailContext);
  if (!ctx) throw new Error("VisitDetail components must be used inside <VisitDetail.Provider>");
  return ctx;
}

// ---------- Pure plumbing provider ----------

function Provider({ value, children }: { value: VisitDetailContextValue; children: React.ReactNode }) {
  return <VisitDetailContext value={value}>{children}</VisitDetailContext>;
}

// ---------- Controller hook (mutations + dialogs) ----------

interface ControllerArgs {
  visit: VisitDto;
  orderSet?: PresentedOrderSetDto;
  /** Lifecycle actions. Pass null for read-only contexts. */
  actions: VisitLifecycleActions | null;
  scheduleId?: string | null;
  onActionComplete?: () => void;
}

export function useVisitDetailController({
  visit,
  orderSet,
  actions,
  scheduleId = null,
  onActionComplete,
}: ControllerArgs): {
  value: VisitDetailContextValue;
  dialogs: React.ReactNode;
} {
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const now = useNow();
  const loadedOrderSet = useOrderSet(
    !orderSet && visit.status === "inProgress" && visit.kind !== "consultation"
      ? { visitId: visit.visitId }
      : undefined,
  );

  const runAction = async (promise: Promise<unknown>, successMessage: string) => {
    try {
      await promise;
      toast.success(successMessage);
      onActionComplete?.();
    } catch {
      // Domain errors surface globally via the tRPC error link.
    }
  };

  // `runAction` is an unmemoized closure over toast/onActionComplete — adding
  // it to deps would defeat the memo for no behavior change.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above
  const menuItems = useMemo<VisitDetailMenuItem[]>(() => {
    if (!actions) return [];
    const states = getAppointmentActionStates(visit, now);
    const completionReason = visitCompletionReason(visit, orderSet ?? loadedOrderSet);
    const isTerminal = visit.status === "cancelled" || visit.status === "noShow" || visit.status === "completed";
    if (isTerminal) return [];

    const items: VisitDetailMenuItem[] = [];
    const cancelItem: VisitDetailMenuItem = {
      label: "Cancel",
      ...states.cancel,
      variant: "destructive",
      onClick: () => setCancelOpen(true),
    };

    if (visit.status === "booked") {
      items.push({
        label: "Check In",
        ...states.checkIn,
        onClick: () => runAction(actions.checkIn(), "Visit checked in"),
      });
      if (scheduleId) {
        items.push({ label: "Reschedule", ...states.reschedule, onClick: () => setRescheduleOpen(true) });
      }
      items.push({
        label: "No Show",
        ...states.noShow,
        onClick: () => runAction(actions.noShow(), "Visit marked as no-show"),
      });
      items.push(cancelItem);
    } else if (visit.status === "checkedIn") {
      items.push({
        label: "No Show",
        ...states.noShow,
        onClick: () => runAction(actions.noShow(), "Visit marked as no-show"),
      });
      items.push(cancelItem);
    } else if (visit.status === "inProgress") {
      items.push({
        label: "Finalize visit",
        ...states.complete,
        ...(completionReason ? { allowed: false, reason: completionReason } : {}),
        onClick: () => runAction(actions.complete(), "Visit completed"),
      });
      items.push(cancelItem);
      // No "pending" arm: the visit projection folds FHIR's `pending` into
      // `proposed`, so it never reaches the DTO.
    } else if (visit.status === "proposed") {
      items.push(cancelItem);
    }
    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visit, orderSet, loadedOrderSet, actions, scheduleId, now]);

  const value: VisitDetailContextValue = {
    state: { visit, ...(orderSet ? { orderSet } : {}) },
    actions: { menuItems },
  };

  const onCancelConfirm = () => {
    if (!actions) return;
    setCancelOpen(false);
    runAction(actions.cancel(), "Visit cancelled");
  };

  const dialogs = (
    <>
      <RescheduleDialog
        key={visit.visitId}
        open={rescheduleOpen}
        onOpenChange={setRescheduleOpen}
        visit={visit}
        onSuccess={() => {
          toast.success("Appointment rescheduled");
          onActionComplete?.();
        }}
      />
      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Visit</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel this visit? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Visit</AlertDialogCancel>
            <AlertDialogAction onClick={onCancelConfirm}>Cancel Visit</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  return { value, dialogs };
}

// ---------- Convenience wrappers ----------

/** Default controller for app code: wires data-store mutations as actions. */
export function useDataStoreVisitDetailController(args: Omit<ControllerArgs, "actions">) {
  const actions = useDataStoreVisitActions(args.visit);
  return useVisitDetailController({ ...args, actions });
}

/** Read-only controller: no mutations, no dialogs. */
export function useReadOnlyVisitDetailController(visit: VisitDto): { value: VisitDetailContextValue } {
  return { value: { state: { visit }, actions: { menuItems: [] } } };
}

// ---------- Containers ----------

function Card({ children }: { children: React.ReactNode }) {
  return <UICard size="sm">{children}</UICard>;
}

function CardHeader({ children }: { children: React.ReactNode }) {
  return (
    <UICardHeader>
      <div className="flex items-center justify-between w-full">{children}</div>
    </UICardHeader>
  );
}

function Body({ children }: { children: React.ReactNode }) {
  return (
    <UICardContent>
      <div className="space-y-3">{children}</div>
    </UICardContent>
  );
}

function Inline({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-1 flex-col justify-between">{children}</div>;
}

/**
 * Compact colored block for calendar event cells. `colors` accepts any Tailwind class set
 * with bg/border/text/subtext — caller picks status colors or overlay schedule colors.
 */
const TileAccentContext = createContext<TimelineSurface | null>(null);

/**
 * Publishes the tile's accent and variant to nested TileTitle/TileSubtitle
 * without rendering a block itself — a list row draws its own container but
 * must tint its text from the same table the calendar tile uses.
 */
function TileAccent({ accent, variant, children }: TimelineSurface & { children: React.ReactNode }) {
  return <TileAccentContext value={{ accent, variant }}>{children}</TileAccentContext>;
}

function TileTitle({ children }: { children: React.ReactNode }) {
  const surface = use(TileAccentContext);
  return (
    <div className={cn("text-sm font-medium truncate", surface && ACCENTS[surface.accent ?? "primary"].text)}>
      {children}
    </div>
  );
}

function TileSubtitle({ children }: { children: React.ReactNode }) {
  const surface = use(TileAccentContext);
  return <div className={cn("text-xs truncate", surface && timelineSubtext(surface))}>{children}</div>;
}

function InlineFields({ children }: { children: React.ReactNode }) {
  return <div className="space-y-4">{children}</div>;
}

function InlineActions({ children }: { children: React.ReactNode }) {
  return <div className="flex items-center justify-end gap-2 pt-4">{children}</div>;
}

// ---------- Presentational subcomponents ----------

function StatusBadge() {
  const {
    state: { visit },
  } = useVisitDetailContext();
  // A running encounter outranks "Checked In": both are true, but the clinician
  // is asking whether the visit is under way, not whether the patient arrived.
  const status = visit.encounterStatus === "in-progress" ? "in-progress" : visit.status;
  return <AppointmentStatusBadge status={status} />;
}

/** Renders the "Join" action for a virtual (`VR`) visit; nothing otherwise (MIN-283). */
function JoinAction({ iconOnly = false }: { iconOnly?: boolean }) {
  const {
    state: { visit },
  } = useVisitDetailContext();
  return <JoinVisitButton visit={visit} iconOnly={iconOnly} />;
}

function ActionsMenu() {
  const {
    actions: { menuItems },
  } = useVisitDetailContext();
  const items = menuItems;
  if (items.length === 0) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button variant="ghost" size="icon-xs">
            <EllipsisIcon className="size-4" />
          </Button>
        }
      />
      <DropdownMenuContent align="end">
        {items.map((item) =>
          item.allowed ? (
            <DropdownMenuItem key={item.label} variant={item.variant} onClick={item.onClick}>
              {item.label}
            </DropdownMenuItem>
          ) : (
            // The reason is rendered, not tooltipped: a disabled item takes no
            // focus, so a hover-only reason has no keyboard or screen-reader path.
            <DropdownMenuItem key={item.label} disabled variant={item.variant} className="flex-col items-start gap-0.5">
              <span>{item.label}</span>
              <span className="text-muted-foreground text-xs">{item.reason ?? "Not available"}</span>
            </DropdownMenuItem>
          ),
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-muted-foreground text-xs">{label}</div>
      <div className="text-sm">{value}</div>
    </div>
  );
}

function DateField() {
  const {
    state: { visit },
  } = useVisitDetailContext();
  if (!visit.start) return null;
  return <Field label="Date" value={formatDateFull(visit.start)} />;
}

function TimeField() {
  const {
    state: { visit },
  } = useVisitDetailContext();
  if (!visit.start || !visit.end) return null;
  return <Field label="Time" value={`${formatTime(visit.start)} - ${formatTime(visit.end)}`} />;
}

/**
 * Progress through the booked window, shown only while the visit is underway.
 * Past the scheduled end the bar pins at full and turns amber rather than
 * counting a negative remainder.
 */
function Elapsed() {
  const {
    state: { visit },
  } = useVisitDetailContext();
  const now = useNow();
  if (visit.status !== "inProgress" && visit.encounterStatus !== "in-progress") return null;
  const progress = projectVisitElapsed({ start: visit.start, end: visit.end, now });
  if (!progress) return null;

  const lead = progress.pending
    ? `Starts in ${progress.startsInMinutes} min`
    : `${progress.elapsedMinutes} min elapsed`;
  const remainder = progress.pending
    ? `${progress.remainingMinutes} min scheduled`
    : progress.overrun
      ? `${progress.overMinutes} min over`
      : `${progress.remainingMinutes} min to scheduled end`;

  return (
    // The track renders last in the composed Progress, so flex `order` lifts it
    // above its labels the way the design has it.
    <Progress
      value={progress.percent}
      className={cn(
        "min-w-0 gap-1 [&_[data-slot=progress-track]]:order-1 [&_[data-slot=progress-track]]:h-1",
        progress.overrun && "[&_[data-slot=progress-indicator]]:bg-warning",
      )}
    >
      <ProgressLabel className="order-2 font-normal text-muted-foreground text-xs tabular-nums">{lead}</ProgressLabel>
      <span
        className={cn(
          "order-2 ml-auto text-xs tabular-nums",
          progress.overrun ? "font-medium text-warning" : "text-muted-foreground",
        )}
      >
        {remainder}
      </span>
    </Progress>
  );
}

function useProtocolRow() {
  const {
    state: { visit, orderSet },
  } = useVisitDetailContext();
  const title = orderSet?.title ?? visit.protocolTitle ?? visit.serviceType?.display ?? visit.serviceType?.text ?? null;
  if (!title) return null;
  return {
    title,
    protocolDefinitionUrl: orderSet?.protocol?.url ?? visit.protocolDefinitionUrl,
    orderedAt: orderSet?.orderedAt,
  };
}

/** The protocol as a labelled field, for panels that list a visit's attributes. */
function Protocol() {
  const row = useProtocolRow();
  if (!row) return null;
  return <ProtocolDetailRow {...row} />;
}

/** The protocol as the block's headline — what the visit IS, not one field among many. */
function ProtocolHeadline() {
  const row = useProtocolRow();
  if (!row) return null;
  return <ProtocolDetailRow {...row} headline />;
}

/**
 * Date, time and clinician as one compact line. The design's encounter block
 * leads with what the visit is; the particulars ride underneath as supporting
 * detail rather than as three labelled fields of equal weight.
 */
function MetaLine() {
  const {
    state: { visit },
  } = useVisitDetailContext();
  const parts: React.ReactNode[] = [];
  if (visit.start) parts.push(formatDateCompact(visit.start));
  if (visit.start && visit.end) parts.push(`${formatTime(visit.start)} – ${formatTime(visit.end)}`);
  if (visit.practitionerId)
    parts.push(
      <PractitionerDisplay
        practitionerId={visit.practitionerId}
        fallbackDisplay={visit.practitionerName}
        showRole={false}
      />,
    );
  if (parts.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-muted-foreground text-xs tabular-nums">
      {parts.map((part, i) => (
        // Positional: these are fixed slots, not a reorderable list.
        // biome-ignore lint/suspicious/noArrayIndexKey: see comment above
        <Fragment key={i}>
          {i > 0 && <span className="text-muted-foreground/50">·</span>}
          {part}
        </Fragment>
      ))}
    </div>
  );
}

function PatientField() {
  const {
    state: { visit },
  } = useVisitDetailContext();
  if (!visit.patientName) return null;
  return <Field label="Patient" value={visit.patientName} />;
}

function Practitioner() {
  const {
    state: { visit },
  } = useVisitDetailContext();
  if (!visit.practitionerId) return null;
  return (
    <Field
      label="Provider"
      value={<PractitionerDisplay practitionerId={visit.practitionerId} fallbackDisplay={visit.practitionerName} />}
    />
  );
}

/** Bare patient name text, no label. Falls back to nothing if unset. */
function PatientName() {
  const {
    state: { visit },
  } = useVisitDetailContext();
  return <>{visit.patientName ?? "Appointment"}</>;
}

/** Bare formatted date text, no label. */
function DateText() {
  const {
    state: { visit },
  } = useVisitDetailContext();
  if (!visit.start) return null;
  return <>{formatDateFull(visit.start)}</>;
}

/** Bare protocol/serviceType title text, no label and no hover card. */
function ProtocolTitle() {
  const {
    state: { visit, orderSet },
  } = useVisitDetailContext();
  const title = orderSet?.title ?? visit.protocolTitle ?? visit.serviceType?.display ?? visit.serviceType?.text ?? null;
  if (!title) return null;
  return <>{title}</>;
}

/** Bare time range (e.g. "10:00 AM - 11:00 AM"), no label. For compact tile layouts. */
function TimeRange() {
  const {
    state: { visit },
  } = useVisitDetailContext();
  if (!visit.start || !visit.end) return null;
  return <>{`${formatTime(visit.start)} - ${formatTime(visit.end)}`}</>;
}

/**
 * The visit's timed work on the same timeline the protocol detail draws, so a
 * plan and its realized visit read identically.
 *
 * This replaces a panel that plotted SLOTS. Slots are reservations, and only a
 * resource-bound procedure makes one — so that panel showed a fraction of the
 * visit (15 of 60 minutes on a seeded plan) under a heading that read like the
 * whole of it, while silently omitting every procedure that needs no equipment.
 * Steps are the full scope; a reservation is a detail hung on the procedure
 * holding it.
 */
function Timeline() {
  const {
    state: { visit, orderSet },
  } = useVisitDetailContext();
  if (!orderSet) return <AppointmentTimeline visit={visit} />;

  const segments = (visit.segments ?? []).flatMap((segment) =>
    segment.start && segment.end ? [{ ...segment, start: segment.start, end: segment.end }] : [],
  );
  const visitStart = visit.start ? new Date(visit.start).getTime() : undefined;

  /** The reservation covering this procedure's slice of the visit, if one does. */
  function resourceFor(offsetMinutes: number, durationMinutes: number) {
    if (visitStart === undefined) return undefined;
    const from = visitStart + offsetMinutes * 60000;
    const to = from + durationMinutes * 60000;
    const hit = segments.find(
      (segment) => new Date(segment.start).getTime() < to && new Date(segment.end).getTime() > from,
    );
    const name = hit?.deviceName ?? hit?.deviceId ?? hit?.locationName ?? hit?.locationId;
    if (!name) return undefined;
    const Icon = hit?.deviceName || hit?.deviceId ? CpuIcon : MapPinIcon;
    return (
      <span className="inline-flex min-w-0 items-center gap-1">
        <Icon className="size-3 shrink-0" />
        <span className="truncate">{name}</span>
      </span>
    );
  }

  const steps = orderSet.steps.map((step) => {
    const orders = step.orders.filter((order) => !isWithdrawnProcedureOrder(order));
    const costMinutes = step.placement.durationMinutes;
    const startMinutes = step.placement.offsetMinutes;
    return {
      id: step.stepId,
      startMinutes,
      costMinutes,
      procedures: orders.map((order) => {
        return {
          id: order.orderId,
          title: order.title,
          variant: CHARTING_STATUSES[chartingStatusOf(orderProcedureStatus(order), order.status)].timeline,
          detail: resourceFor(startMinutes, costMinutes),
        };
      }),
    };
  });

  if (steps.length === 0) return <AppointmentTimeline visit={visit} />;
  const totalDurationMinutes = Math.max(0, ...steps.map((step) => step.startMinutes + step.costMinutes));
  return (
    <TimelineProvider
      density="compact"
      steps={steps}
      envelopeMinutes={Math.max(orderSet.envelopeMinutes, totalDurationMinutes)}
      {...(visit.start ? { startsAt: visit.start } : {})}
    >
      <TimelinePanelLayout />
    </TimelineProvider>
  );
}

function Reason() {
  const {
    state: { visit },
  } = useVisitDetailContext();
  if (!visit.description) return null;
  return <Field label="Reason" value={visit.description} />;
}

export const VisitDetail = {
  Provider,
  Card,
  CardHeader,
  Body,
  Inline,
  InlineFields,
  InlineActions,
  TileAccent,
  TileTitle,
  TileSubtitle,
  StatusBadge,
  JoinAction,
  ActionsMenu,
  Date: DateField,
  Time: TimeField,
  Elapsed,
  MetaLine,
  ProtocolHeadline,
  TimeRange,
  DateText,
  ProtocolTitle,
  PatientName,
  Protocol,
  Patient: PatientField,
  Practitioner,
  Reason,
  Timeline,
};

// ---------- Protocol row ----------

function ProtocolDetailRow({
  title,
  protocolDefinitionUrl,
  orderedAt,
  headline = false,
}: {
  title: string;
  protocolDefinitionUrl?: string;
  /**
   * When the protocol was materialized onto the order set. The chart links to
   * the protocol AS ORDERED, not as it stands today — a set that has been
   * ordered no longer follows the catalog, and showing today's protocol beside
   * this visit's work would describe a plan nobody ordered.
   *
   * Absent for a visit with no order set, which falls back to the live view.
   */
  orderedAt?: string;
  /** Drop the "Protocol" label and lead with the title at heading weight. */
  headline?: boolean;
}) {
  const asOfSearch = orderedAt ? { asOf: orderedAt } : {};

  return (
    <div className="min-w-0">
      {!headline && <div className="text-muted-foreground text-xs">Protocol</div>}
      <div className={cn(headline ? "font-semibold text-[15px] leading-snug tracking-tight" : "text-sm")}>
        {protocolDefinitionUrl ? (
          <Link
            to="/catalog/protocols/$protocolId"
            params={{ protocolId: protocolDefinitionRouteId(protocolDefinitionUrl) }}
            search={asOfSearch}
            className="line-clamp-1 underline decoration-muted-foreground/40 underline-offset-4 hover:decoration-current"
          >
            {title}
          </Link>
        ) : (
          <span className="line-clamp-1">{title}</span>
        )}
      </div>
    </div>
  );
}
